// Fase 2, corte 7 (#0746, ADR-0150) — el contacto para visitas de la
// unidad: quién recibe al contratista cuando la unidad está arrendada.
//
// Es un dato de un TERCERO (el arrendatario) que entrega el propietario
// (informe de factibilidad :96, Ley 21.719): vive en su propia tabla
// (`unit_visit_contacts`), nunca en `assets.attributes`; lo escribe sólo
// el propietario, declarando que le informó; vence (`validUntil`) y un
// job lo borra. Este módulo es la ÚNICA definición de sus reglas de
// forma — el body de `PUT /assets/:id/visit-contact`, la hoja de la PWA
// y los textos que lo muestran al personal y al contratista.
import { addCalendarDays, addCalendarMonths, santiagoDate } from "@bitacorapro/core-engine";
import { z } from "zod";
/** Hoy una sola relación. Otra (cuidador, administrador) es otro valor
 *  del CHECK de la tabla y de esta lista, no texto libre. */
export const VISIT_CONTACT_RELATIONS = ["arrendatario"];
/** «Arrendatario/a»: el propietario no declara género y la app no lo
 *  infiere del nombre. */
export const VISIT_CONTACT_RELATION_LABEL = {
    arrendatario: "Arrendatario/a",
};
export const VISIT_CONTACT_NAME_MIN = 2;
export const VISIT_CONTACT_NAME_MAX = 80;
/** Vigencia por defecto y máxima, en meses desde hoy (Santiago). */
export const VISIT_CONTACT_DEFAULT_MONTHS = 12;
export const VISIT_CONTACT_MAX_MONTHS = 24;
/** Días que una fila quitada por el propietario espera antes de la purga
 *  física (ADR-0150): margen para auditar un borrado reclamado, nunca
 *  visible para nadie mientras tanto. */
export const VISIT_CONTACT_DELETED_RETENTION_DAYS = 30;
/** La casilla obligatoria de la hoja. El servidor no recibe el texto,
 *  sólo `informed: true`; vive acá para que la PWA y el ADR citen la
 *  misma frase. Pendiente de revisión legal (founder, 2026-10-16). */
export const VISIT_CONTACT_INFORMED_COPY = "Informé a mi arrendatario que la inmobiliaria usará este contacto solo para coordinar visitas de postventa";
// ── Teléfono chileno ─────────────────────────────────────────────────
//
// Se guarda NORMALIZADO, «+56 9 1234 5678»: el personal lo lee y lo
// marca, el contratista lo toca como `tel:`. Acepta lo que la gente
// escribe (espacios, guiones, puntos, paréntesis, con o sin +56) y
// rechaza todo lo que no termine en 9 dígitos chilenos.
const PHONE_CHARS = /^\+?[\d\s().-]+$/;
/** «+56 9 1234 5678», o null si no es un número chileno de 9 dígitos
 *  (celular 9XXXXXXXX o fijo con código de área, p. ej. 2XXXXXXXX). */
export function normalizeChilePhone(raw) {
    const value = raw.trim();
    if (value.length === 0 || value.length > 40 || !PHONE_CHARS.test(value))
        return null;
    let digits = value.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("56"))
        digits = digits.slice(2);
    else if (digits.length === 13 && digits.startsWith("0056"))
        digits = digits.slice(4);
    if (digits.length !== 9 || digits.startsWith("0"))
        return null;
    return `+56 ${digits[0]} ${digits.slice(1, 5)} ${digits.slice(5)}`;
}
// ── Vigencia ─────────────────────────────────────────────────────────
function isCalendarDate(date) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date))
        return false;
    const [y, m, d] = date.split("-").map(Number);
    const t = new Date(Date.UTC(y, m - 1, d));
    return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}
/** Suma meses de calendario (ADR-0153 la movió al engine; se reexporta
 *  aquí para los consumidores de la vigencia). */
export { addCalendarMonths };
/** Los bordes de la vigencia para hoy (Santiago): estrictamente después
 *  de hoy y a lo más `VISIT_CONTACT_MAX_MONTHS` meses. */
export function visitContactValidUntilBounds(now) {
    const today = santiagoDate(now);
    return {
        min: addCalendarDays(today, 1),
        max: addCalendarMonths(today, VISIT_CONTACT_MAX_MONTHS),
        default: addCalendarMonths(today, VISIT_CONTACT_DEFAULT_MONTHS),
    };
}
/** ¿Vale esa fecha como vigencia hoy? «AAAA-MM-DD» ordena igual que el
 *  calendario. */
export function visitContactValidUntilAllowed(date, now) {
    if (!isCalendarDate(date))
        return false;
    const { min, max } = visitContactValidUntilBounds(now);
    return date >= min && date <= max;
}
// ── Wire ─────────────────────────────────────────────────────────────
/** PUT /assets/:id/visit-contact — crea o reemplaza el contacto vivo de
 *  la unidad. `informed` es la casilla: sólo `true` pasa. `validUntil`
 *  ausente = hoy + 12 meses; su rango (mañana … hoy + 24 meses) lo
 *  valida la ruta, porque depende del reloj. */
export const visitContactPutBody = z
    .object({
    name: z.string().trim().min(VISIT_CONTACT_NAME_MIN).max(VISIT_CONTACT_NAME_MAX),
    phone: z
        .string()
        .max(40)
        .refine((v) => normalizeChilePhone(v) !== null, {
        message: "phone must be a Chilean number with 9 digits (optionally +56)",
    }),
    relation: z.enum(VISIT_CONTACT_RELATIONS).default("arrendatario"),
    informed: z.literal(true),
    validUntil: z
        .string()
        .refine(isCalendarDate, { message: "validUntil must be a calendar day (YYYY-MM-DD)" })
        .optional(),
})
    .strict();
/** El contacto, tal como lo ve el propietario y el personal. */
export const unitVisitContactSchema = z
    .object({
    name: z.string(),
    phone: z.string(),
    relation: z.enum(VISIT_CONTACT_RELATIONS),
    /** Hasta qué día vale (inclusive). Pasado ese día ya no se muestra a
     *  nadie y el job lo borra. */
    validUntil: z.string(),
    /** Cuándo el propietario declaró que le informó al arrendatario. */
    informedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const visitContactResponse = z
    .object({ visitContact: unitVisitContactSchema.nullable() })
    .strict();
/** La proyección mínima para el contratista (`/r/b`): nombre y teléfono,
 *  nada más. */
export const publicVisitContactSchema = z.object({ name: z.string(), phone: z.string() }).strict();
/** «{nombre} · Arrendatario/a». */
export function visitContactTitle(c) {
    return `${c.name} · ${VISIT_CONTACT_RELATION_LABEL[c.relation]}`;
}
/** La línea del personal en el detalle y al asignar:
 *  «Arrendada · coordinar con {nombre} {teléfono}». */
export function visitContactCoordinationLine(c) {
    return `Arrendada · coordinar con ${c.name} ${c.phone}`;
}
//# sourceMappingURL=visit-contact.js.map